import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Button, Grid } from '@mui/material';

import useTabContext from 'common/hooks/useTabContext';
import { PATIENT_RELAY_ORDERS_FAILED_MESSAGE } from 'api/relay';
import RelayOrdersTableView from 'modules/WorkQueue/components/RelayOrdersTableView';
import RelayOrderDetailsDialog from 'modules/WorkQueue/components/RelayOrderDetailsDialog';

import usePatientRelayOrders from './usePatientRelayOrders';

// The tabValue PatientDetailsTabs registers this tab under.
const PATIENT_RELAY_ORDERS_TAB = 'PatientRelayOrdersTab';

// Every row is this patient, so the Patient column would repeat the page's own header. Partner
// stays: one patient can come through more than one partner.
const HIDDEN_COLUMNS = ['patientName'];

// The read is TRANSFERRED-only, the queue's scope, so "nothing" has to say which nothing it means:
// a patient with a relay still in flight would otherwise read as having none at all.
const EMPTY_MESSAGE = 'No transferred relay orders for this patient.';

/**
 * POM-6279 — this patient's relay orders: the Central Fill queue's Relay table and its details
 * dialog, shared rather than copied, with no filter and no mode switch -- the patient IS the
 * filter. POM sys-admin only; PatientDetailsTabs does not mount this for anyone else.
 */
const PatientRelayOrdersTab = () => {
  const { mpi } = useParams();
  const isActive = useTabContext().tab === PATIENT_RELAY_ORDERS_TAB;
  const { rows, count, page, onPageChange, retry, failed } = usePatientRelayOrders({
    mpi,
    isActive,
  });
  // Which relay order's details are open, if any, and for WHICH PATIENT. Held here rather than in
  // the table so the dialog survives a page change underneath it, as it does in the queue. Stored
  // with the mpi because PatientView is not keyed by mpi: browser back/forward from A to B keeps
  // this component mounted, and an id alone would leave A's order open over B's page.
  const [details, setDetails] = useState(null);
  const openRelayOrderId = details?.mpi === mpi ? details.relayOrderId : null;

  const openDetails = useCallback((relayOrderId) => setDetails({ mpi, relayOrderId }), [mpi]);
  const closeDetails = useCallback(() => setDetails(null), []);

  if (!isActive) {
    return null;
  }

  return (
    <Grid container direction='column'>
      <Grid item>
        {failed ? (
          <Alert
            severity='error'
            action={
              <Button color='inherit' size='small' onClick={retry}>
                Retry
              </Button>
            }
          >
            {PATIENT_RELAY_ORDERS_FAILED_MESSAGE}
          </Alert>
        ) : (
          <RelayOrdersTableView
            rows={rows}
            count={count}
            page={page}
            onPageChange={onPageChange}
            onOpenDetails={openDetails}
            hiddenColumns={HIDDEN_COLUMNS}
            emptyMessage={EMPTY_MESSAGE}
          />
        )}
      </Grid>
      {/* Keyed by order id so opening a different order remounts the dialog rather than reusing a
          component still holding the previous order's details. */}
      <RelayOrderDetailsDialog
        key={openRelayOrderId}
        relayOrderId={openRelayOrderId}
        onClose={closeDetails}
      />
    </Grid>
  );
};

export default PatientRelayOrdersTab;
